import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { api } from "./api";

// All /v1/* calls (except /v1/workspaces and /v1/me) require a workspace
// scope — and most also require a project. The scope lives in
// localStorage as UUIDs so reloads keep the user on the same project.
//
// Slugs are deliberately NOT stored here — every wire-level identifier
// the client sends to the server is a UUID, and slugs are only
// rendered for display, fetched from /v1/workspaces and /v1/projects.

const WORKSPACE_ID_KEY = "memkernel.scope.workspaceId";
const PROJECT_ID_KEY = "memkernel.scope.projectId";

export interface Scope {
  workspaceId: string | null;
  projectId: string | null;
}

interface ScopeContextValue extends Scope {
  setWorkspaceId: (id: string | null) => void;
  setProjectId: (id: string | null) => void;
}

// Module-level cache so the request interceptor in api.ts can read it
// synchronously without going through React state.
let liveScope: Scope = {
  workspaceId: readStorage(WORKSPACE_ID_KEY),
  projectId: readStorage(PROJECT_ID_KEY),
};

export function currentScope(): Scope {
  return liveScope;
}

// Synchronous + non-React. Used by sign-out so we don't leak the previous
// user's workspace/project into a fresh login.
export function clearScope(): void {
  liveScope = { workspaceId: null, projectId: null };
  writeStorage(WORKSPACE_ID_KEY, null);
  writeStorage(PROJECT_ID_KEY, null);
}

function readStorage(key: string): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: string | null) {
  if (typeof window === "undefined") return;
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch {
    // localStorage may be disabled — silently fall back to in-memory.
  }
}

const ScopeContext = createContext<ScopeContextValue | null>(null);

export function ScopeProvider({ children }: { children: ReactNode }) {
  const [workspaceId, setWorkspaceIdState] = useState<string | null>(
    liveScope.workspaceId,
  );
  const [projectId, setProjectIdState] = useState<string | null>(
    liveScope.projectId,
  );

  const setWorkspaceId = useCallback((id: string | null) => {
    liveScope = { ...liveScope, workspaceId: id };
    writeStorage(WORKSPACE_ID_KEY, id);
    setWorkspaceIdState(id);
  }, []);

  const setProjectId = useCallback((id: string | null) => {
    liveScope = { ...liveScope, projectId: id };
    writeStorage(PROJECT_ID_KEY, id);
    setProjectIdState(id);
  }, []);

  const value = useMemo<ScopeContextValue>(
    () => ({
      workspaceId,
      projectId,
      setWorkspaceId,
      setProjectId,
    }),
    [workspaceId, projectId, setWorkspaceId, setProjectId],
  );

  return (
    <ScopeContext.Provider value={value}>{children}</ScopeContext.Provider>
  );
}

export function useScope(): ScopeContextValue {
  const ctx = useContext(ScopeContext);
  if (!ctx) {
    throw new Error("useScope must be used inside <ScopeProvider>");
  }
  return ctx;
}

interface MeResponse {
  id: string;
  email: string;
  name: string;
  defaults: { workspaceId: string; projectId: string };
}

/**
 * On every console mount: ensure scope is initialised AND points at
 * something the current user actually owns. /v1/me always returns the
 * user's first workspace + first project (provisioning them on demand if
 * missing) — we treat its `defaults` as the source of truth on first
 * load and any time localStorage looks stale (e.g. previous account's
 * workspace id, or after the row was deleted). If the stored values
 * still resolve for this user we keep them so the user stays on
 * whichever project they last picked.
 */
export function useBootstrapScope() {
  const { workspaceId, projectId, setWorkspaceId, setProjectId } = useScope();
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api<MeResponse>("/v1/me")
      .then(async (me) => {
        if (cancelled) return;
        // Seed if nothing is stored.
        if (!workspaceId) {
          setWorkspaceId(me.defaults.workspaceId);
          setProjectId(me.defaults.projectId);
          setReady(true);
          return;
        }
        // Stored — verify it still resolves. A 4xx on the workspace
        // means the id is stale (different account, deleted ws); fall
        // back to defaults silently.
        try {
          await api(`/v1/workspaces/${workspaceId}`);
          if (!projectId) setProjectId(me.defaults.projectId);
        } catch {
          if (!cancelled) {
            setWorkspaceId(me.defaults.workspaceId);
            setProjectId(me.defaults.projectId);
          }
        }
        if (!cancelled) setReady(true);
      })
      .catch(() => {
        if (!cancelled) setReady(true);
      });
    return () => {
      cancelled = true;
    };
    // Only run on mount — subsequent scope changes are user-driven via
    // the switcher and shouldn't re-trigger the reconciliation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return ready;
}
